import first from "lodash/first";
import { numify } from "numify";
import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { newsletter as newsletterData } from "shared/data/newsletter";
import { notEmpty } from "shared/lib/utils";

import IconClock from "scmp-app/components/newsletter/newsletter-category-item-slideshow/icon-clock.svg";
import IconUser from "scmp-app/components/newsletter/newsletter-category-item-slideshow/icon-user.svg";
import Placeholder from "scmp-app/components/newsletter/newsletter-category-item-slideshow/placeholder.svg";
import type { plusNewsletterItemNewsletter$key } from "scmp-app/queries/__generated__/plusNewsletterItemNewsletter.graphql";

import {
  Container,
  Content,
  ContentHeader,
  Description,
  Flag,
  Footer,
  ImageContainer,
  Name,
  NumberOfSignedUpUsers,
  PlaceholderContainer,
  Preview,
  StyledBaseImage,
  Summary,
} from "./styles";

type Props = {
  className?: string;
  reference: plusNewsletterItemNewsletter$key;
};

export const PlusNewsletterItem: FunctionComponent<Props> = ({ className, reference }) => {
  const newsletter = useFragment(
    graphql`
      fragment plusNewsletterItemNewsletter on Newsletter {
        name
        numberOfSignedUpUsers
        entityId
        images {
          title
          url
        }
        summary {
          text
        }
        description {
          text
        }
        shortDescription {
          text
        }
        ...entityLink
        ...signUpButtonNewsletter
      }
    `,
    reference,
  );

  const renderImage = useCallback(() => {
    const newsletterImage = first(newsletter.images ?? []);

    if (notEmpty(newsletterImage?.url)) {
      return (
        <ImageContainer>
          <StyledBaseImage
            $aspectRatio="3/2"
            $objectFit="cover"
            alt={newsletterImage?.title ?? newsletter.name ?? ""}
            src={newsletterImage.url}
          />
          {newsletter.shortDescription?.text && <Flag>{newsletter.shortDescription.text}</Flag>}
        </ImageContainer>
      );
    }
    return (
      <PlaceholderContainer>
        <Placeholder />
      </PlaceholderContainer>
    );
  }, [newsletter.images, newsletter.name, newsletter.shortDescription?.text]);

  return (
    <Container className={className}>
      {renderImage()}

      <Content>
        <ContentHeader>
          {newsletter.summary?.text && (
            <Summary>
              <IconClock />
              {newsletter.summary?.text}
            </Summary>
          )}
          {newsletter.numberOfSignedUpUsers && (
            <NumberOfSignedUpUsers>
              <IconUser />
              {numify(newsletter.numberOfSignedUpUsers)}
            </NumberOfSignedUpUsers>
          )}
        </ContentHeader>
        {newsletter.name && <Name>{newsletter.name}</Name>}

        {newsletter.description?.text && <Description>{newsletter.description?.text}</Description>}
      </Content>
      {newsletterData.dailyPulse.entityId === newsletter.entityId && (
        <Footer>
          <Preview pathname="https://subscribe.scmp.com/plus" target="_blank">
            Find out more
          </Preview>
        </Footer>
      )}
    </Container>
  );
};

PlusNewsletterItem.displayName = "PlusNewsletterItem";
